import React from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  CATALOG_LICENSE_INFO_URL,
  CREATIVE_COMMONS_LICENSES_URL,
  formatSourceLinkLabel,
  getLicenseLink
} from '../../lib/catalogSampleDisplay';

export function CatalogSampleLicenseDisclaimer({ className }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-amber-200/80 bg-amber-50/80 p-3.5 text-xs leading-relaxed text-amber-950',
        'dark:border-amber-900/50 dark:bg-amber-950/25 dark:text-amber-100',
        className
      )}
    >
      <p>
        Sample datasets and workspaces are provided as-is for demonstration, with no guarantees as
        to accuracy, completeness, or fitness for any particular use. You are responsible for
        complying with each dataset&apos;s license before use. See{' '}
        <a
          href={CATALOG_LICENSE_INFO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-200"
        >
          choosing a license
        </a>{' '}
        and{' '}
        <a
          href={CREATIVE_COMMONS_LICENSES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-200"
        >
          Creative Commons licenses
        </a>
        .
      </p>
    </div>
  );
}

function catalogMetaStopClick(stopPropagation) {
  return stopPropagation
    ? (e) => {
        e.stopPropagation();
      }
    : undefined;
}

export function CatalogSourceUrlLine({
  url,
  className,
  stopPropagation = false,
  showLabel = true
}) {
  if (!url) return null;

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {showLabel ? (
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-gray-500">
          Source
        </span>
      ) : null}
      <a
        href={url}
        title={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={catalogMetaStopClick(stopPropagation)}
        className="inline-flex max-w-full items-center gap-1 text-[11px] font-medium text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-400"
      >
        <Globe size={12} className="shrink-0" aria-hidden />
        <span className="truncate">{formatSourceLinkLabel(url)}</span>
        <ExternalLink size={11} className="shrink-0 opacity-70" aria-hidden />
      </a>
    </div>
  );
}

export function CatalogLicenseLine({
  license,
  className,
  stopPropagation = false,
  showLabel = true
}) {
  if (!license) return null;

  const link = getLicenseLink(license);
  const stopClick = catalogMetaStopClick(stopPropagation);

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {showLabel ? (
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-gray-500">
          License
        </span>
      ) : null}
      {link ? (
        <a
          href={link.href}
          title={license}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopClick}
          className="inline-flex max-w-full items-center gap-1 text-[11px] font-medium text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-400"
        >
          <span className="truncate">{link.label}</span>
          <ExternalLink size={11} className="shrink-0" aria-hidden />
        </a>
      ) : (
        <span className="line-clamp-2 text-[11px] leading-snug text-slate-600 dark:text-gray-400" title={license}>
          {license}
        </span>
      )}
    </div>
  );
}

export function CatalogDatasetMeta({ url, license, stopPropagation = false, className }) {
  if (!url && !license) return null;

  return (
    <div
      className={cn(
        'mt-auto flex flex-col gap-1.5 border-t border-slate-100 pt-2 dark:border-gray-800',
        className
      )}
    >
      <CatalogSourceUrlLine url={url} stopPropagation={stopPropagation} />
      <CatalogLicenseLine license={license} stopPropagation={stopPropagation} />
    </div>
  );
}
